/**
 * ViewModel: Reward Catalog
 * Presentation logic for reward catalog feature.
 */

import type { Dispatch, SetStateAction } from "react";
import type {
  VoucherListItem,
  VoucherListQuery,
} from "../../domain/model/RewardCatalogModel";
import { FetchVoucherListUseCase } from "../../domain/usecase/FetchRewardCatalogUseCase";
import { DeleteVoucherUseCase } from "../../domain/usecase/DeleteVoucherUseCase";

export interface PagingInfo {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
}

export interface RewardCatalogViewModelParams {
  setItems: Dispatch<SetStateAction<VoucherListItem[]>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setPaging: Dispatch<SetStateAction<PagingInfo>>;
  setError: Dispatch<SetStateAction<string | null>>;
}

export const RewardCatalogViewModel = ({
  setItems,
  setIsLoading,
  setPaging,
  setError,
}: RewardCatalogViewModelParams) => {
  const loadCatalog = async (query?: VoucherListQuery) => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await FetchVoucherListUseCase(query);
      setItems([...result.items]);
      setPaging(result.paging);
    } catch (error) {
      console.error("Failed to load reward catalog:", error);
      setItems([]);
      setError(
        error instanceof Error ? error.message : "Failed to load reward catalog"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const deleteVoucher = async (voucherId: string): Promise<boolean> => {
    try {
      await DeleteVoucherUseCase(voucherId);
      return true;
    } catch (error) {
      console.error("Failed to delete voucher:", error);
      setError(
        error instanceof Error ? error.message : "Failed to delete voucher. Please try again."
      );
      return false;
    }
  };

  return {
    loadCatalog,
    deleteVoucher,
  };
};
